import AutoAwesomeMosaicRoundedIcon from "@mui/icons-material/AutoAwesomeMosaicRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import RedoRoundedIcon from "@mui/icons-material/RedoRounded";
import UndoRoundedIcon from "@mui/icons-material/UndoRounded";
import { Box, Button, Chip, IconButton, ToggleButton, ToggleButtonGroup, Tooltip } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Brain } from "@zesty-io/material";
import { useTranslation } from "react-i18next";
import { LanguageSelector } from "../../content-editor/src/app/views/ItemEdit/components/ItemEditHeader/LanguageSelector";
import { InteractionMode, usesLayoutGrammar } from "../hooks/studioTypes";
type LayoutBreadcrumbItem = {
  layoutId?: string;
  label: string;
};
type StudioHeaderProps = {
  onLanguageChange: (langCode: string) => void;
  interactionMode: InteractionMode;
  onInteractionModeChange: (mode: InteractionMode) => void;
  /** Modes this user is entitled to. The switch can never exceed these. */
  availableModes: InteractionMode[];
  /**
   * Whether the mode switch is offered at all. Mode is normally resolved from
   * permissions and not selectable; Zesty staff keep the switch so one account
   * can exercise every surface.
   */
  canSelectMode: boolean;
  selectedLayoutBreadcrumb: LayoutBreadcrumbItem[];
  onLayoutBreadcrumbClick: (layoutId: string) => void;
  pageModelZUID: string;
  pageItemZUID: string;
  unresolvedPath: boolean;
  logoSrc: string;
  showAiButton: boolean;
  isAiPanelOpen: boolean;
  onToggleAiPanel: () => void;
  onFeedbackClick: () => void;
  /** Undo/redo render only when the canvas's bridge supports history. */
  showHistoryControls: boolean;
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
};

// Shortcut hints for the undo/redo tooltips. Key symbols, not copy.
const IS_MAC = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.userAgent);
const UNDO_SHORTCUT = IS_MAC ? "⌘Z" : "Ctrl+Z";
const REDO_SHORTCUT = IS_MAC ? "⇧⌘Z" : "Ctrl+Y";
const MODE_OPTIONS: {
  mode: InteractionMode;
  labelKey: string;
  icon: JSX.Element;
}[] = [{
  mode: "full",
  labelKey: "content.studioModeFull",
  icon: <AutoAwesomeRoundedIcon fontSize="small" />
}, {
  mode: "content",
  labelKey: "content.studioModeContent",
  icon: <EditRoundedIcon fontSize="small" />
}, {
  mode: "layout",
  labelKey: "content.studioModeLayout",
  icon: <AutoAwesomeMosaicRoundedIcon fontSize="small" />
}];
export const StudioHeader = ({
  onLanguageChange,
  interactionMode,
  onInteractionModeChange,
  availableModes,
  canSelectMode,
  selectedLayoutBreadcrumb,
  onLayoutBreadcrumbClick,
  pageModelZUID,
  pageItemZUID,
  unresolvedPath,
  logoSrc,
  showAiButton,
  isAiPanelOpen,
  onToggleAiPanel,
  onFeedbackClick,
  showHistoryControls,
  canUndo,
  canRedo,
  onUndo,
  onRedo
}: StudioHeaderProps) => {
  const {
    t
  } = useTranslation();
  const [codeIdSegment, ...pathSegments] = selectedLayoutBreadcrumb;
  return <Box data-cy="StudioHeader" sx={{
    py: 1,
    px: 3,
    display: "flex",
    alignItems: "center",
    gap: 1,
    borderBottom: theme => `1px solid ${theme.palette.border}`,
    backgroundColor: theme => theme.palette.grey[50]
  }} data-sentry-element="Box" data-sentry-component="StudioHeader" data-sentry-source-file="StudioHeader.tsx">
      <Box component="img" src={logoSrc} alt="Content One" data-cy="StudioLogo" sx={{
      height: 32
    }} data-sentry-element="Box" data-sentry-source-file="StudioHeader.tsx" />
      <Box flex="1" display="flex" justifyContent="center" minWidth={0} px={2} data-sentry-element="Box" data-sentry-source-file="StudioHeader.tsx">
        {usesLayoutGrammar(interactionMode) && selectedLayoutBreadcrumb.length ? <Box data-cy="StudioBreadcrumbs" display="flex" alignItems="center" gap={0.5} minWidth={0} sx={{
        overflow: "hidden",
        flexWrap: "nowrap"
      }}>
            {codeIdSegment ? <Chip data-cy="StudioBreadcrumbRoot" label={codeIdSegment.label} size="small" variant="filled" sx={{
          flexShrink: 0,
          minWidth: 0,
          maxWidth: 220
        }} /> : null}
            {pathSegments.length ? <Box data-cy="StudioBreadcrumbRail" minWidth={0} sx={{
          maxWidth: 252,
          overflowX: "auto",
          overflowY: "hidden",
          direction: "rtl",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": {
            display: "none"
          }
        }}>
                <Box display="flex" alignItems="center" gap={0.5} sx={{
            width: "max-content",
            minWidth: "100%",
            direction: "ltr"
          }}>
                  {pathSegments.map((segment, index) => {
              const isCurrent = index === pathSegments.length - 1;
              return <Chip data-cy="StudioBreadcrumbChip" key={segment.layoutId || segment.label} label={segment.label} size="small" variant="filled" onClick={() => {
                if (!segment.layoutId) return;
                onLayoutBreadcrumbClick(segment.layoutId);
              }} clickable={Boolean(segment.layoutId)} sx={{
                flexShrink: 0,
                minWidth: 0,
                maxWidth: 220,
                opacity: isCurrent ? 0.56 : 1
              }} />;
            })}
                </Box>
              </Box> : null}
          </Box> : null}
      </Box>
      <Box display="flex" alignItems="center" gap={1.5} data-sentry-element="Box" data-sentry-source-file="StudioHeader.tsx">
        {showHistoryControls ? <Box display="flex" alignItems="center">
            {/* The span keeps the tooltip working while the button is
                disabled, which MUI otherwise drops. */}
            <Tooltip title={`${t("content.studioUndo")} (${UNDO_SHORTCUT})`}>
              <span>
                <IconButton data-cy="StudioUndoButton" size="small" aria-label={t("content.studioUndo")} disabled={!canUndo} onClick={onUndo}>
                  <UndoRoundedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={`${t("content.studioRedo")} (${REDO_SHORTCUT})`}>
              <span>
                <IconButton data-cy="StudioRedoButton" size="small" aria-label={t("content.studioRedo")} disabled={!canRedo} onClick={onRedo}>
                  <RedoRoundedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box> : null}
        <Button data-cy="StudioFeedbackButton" color="inherit" size="small" onClick={onFeedbackClick} sx={{
        fontWeight: 500
      }} data-sentry-element="Button" data-sentry-source-file="StudioHeader.tsx">
          {t("content.feedbackButtonLabel")}
        </Button>
        <Box minWidth={96} data-sentry-element="Box" data-sentry-source-file="StudioHeader.tsx">
          <LanguageSelector modelZUIDOverride={pageModelZUID} itemZUIDOverride={pageItemZUID} onChange={({
          langCode
        }) => {
          if (!langCode) return;
          onLanguageChange(langCode);
        }} disabled={unresolvedPath} data-sentry-element="LanguageSelector" data-sentry-source-file="StudioHeader.tsx" />
        </Box>
        {canSelectMode && availableModes.length > 1 ? <ToggleButtonGroup data-cy="StudioModeToggle" exclusive size="small" value={interactionMode} onChange={(_evt, nextMode: InteractionMode | null) => {
        // An exclusive group emits null when the active button is
        // clicked again. There is no "no mode" state, so ignore it
        // rather than letting it through as a mode change.
        if (!nextMode) return;
        onInteractionModeChange(nextMode);
      }}>
            {MODE_OPTIONS.filter(option => availableModes.includes(option.mode)).map(option => <ToggleButton key={option.mode} value={option.mode} data-cy={`StudioModeToggleOption-${option.mode}`} aria-label={t(option.labelKey)}>
                {option.icon}
              </ToggleButton>)}
          </ToggleButtonGroup> : null}
        {showAiButton ? <Button data-cy="StudioAIButton" size="small" aria-pressed={isAiPanelOpen} endIcon={<Brain fontSize="small" />} onClick={onToggleAiPanel} sx={{
        fontWeight: 500,
        color: isAiPanelOpen ? "primary.main" : "text.secondary",
        ...(isAiPanelOpen && {
          bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
          "&:hover": {
            bgcolor: theme => alpha(theme.palette.primary.main, 0.08)
          }
        })
      }}>
            {t("content.studioAiButton")}
          </Button> : null}
      </Box>
    </Box>;
};